import type { Maybe, ID } from '@/types'

import { clone } from '@/helpers'

export function hasInactive(items: Maybe<any[]>) {
  return !!items?.some((item) => hasId(item) && !isActive(item))
}

export function hasId(item: any) {
  return !!item.id
}

export function isActive(item?: any) {
  return !!item?.active
}

export function toggleActivity(
  items: any[],
  id: Maybe<ID | string>,
  key = 'active'
) {
  const item = findByPropValue(items, id)
  if (!item) return

  item[key] = !item[key]
}

export function enableActivity(items: any[], ids: ID[]) {
  ids.forEach((id) => {
    const item = findByPropValue(items, id)
    if (!item) return

    item.active = true
  })
}

export function disableActivity(items: any[], ids: ID[]) {
  ids.forEach((id) => {
    const item = findByPropValue(items, id)
    if (!item) return

    item.active = false
  })
}

export function removeFromList(items: any[], value: any, key = 'id') {
  const index = findIndexByPropValue(items, value, key)
  if (index === -1) return

  items.splice(index, 1)
}

export function removeFromListBatch(items: any[], ids: ID[]) {
  ids.forEach((id) => removeFromList(items, id))
}

export function getActiveNumber(items: any[]) {
  return items.reduce((number, item) => number + isActive(item), 0)
}

export function pickOnly(items: any[], key: any): any[] {
  return items.map((item) => item[key])
}

export function getSelectedLength(selected?: any[]) {
  return (selected ?? []).filter((item: any) => item.id !== null).length
}

export function pickWhereNotIn(items: any[], values: any[], key = 'id') {
  if (values.length === 0) return items

  return items.filter((item) => !values.includes(item[key]))
}

export function keyBy(items: any[], key: any) {
  return items.reduce((keyed, item) => {
    keyed[item[key]] = item

    return keyed
  }, {})
}

export function findByPropValue<T = any>(
  items: T[],
  value: any,
  key: keyof T | string = 'id'
): Maybe<T> | any {
  return items?.find((item) => item[key as keyof typeof item] === value) ?? null
}

export function findIndexByPropValue<T = any>(
  items: T[],
  value: any,
  key: keyof T | string = 'id'
) {
  return items.findIndex((item) => item[key as keyof typeof item] === value)
}

export function pickWhereIn(
  items: any[],
  values: any[],
  key = 'id',
  replace: Maybe<Record<string, any>> = null
) {
  return values.reduce((result, value) => {
    const defaultItem = replace ? clone(replace) : null
    const item = clone(findByPropValue(items, value, key)) ?? defaultItem

    if (item) result.push(item)

    return result
  }, [])
}

export function prepareSelected(
  items: any[],
  ids: ID[],
  length: number,
  model: any
) {
  const realItems = items.length && ids.length ? pickWhereIn(items, ids) : []

  return Array.from({ length }).map(
    (_item, index) => realItems[index] ?? clone(model)
  )
}
